import {
  useRef,
  useState,
  type Dispatch,
  type RefObject,
  type SetStateAction,
} from 'react';

import { useCallbackRef } from '../../../core/hooks/useCallbackRef.js';
import { useControllableState } from '../../../core/hooks/useControllableState.js';
import { useResizeObserver } from '../../../core/hooks/useResizeObserver.js';

interface UseBreakpointCollapseArgs {
  /** Controlled / uncontrolled props for the collapsed state. */
  collapsed: {
    value: boolean | undefined;
    defaultValue: boolean;
    onChange: ((value: boolean) => void) | undefined;
  };
  /** Container width below which the panel renders as a drawer. */
  breakpoint: number;
  /** Container element observed for size changes. */
  containerRef: RefObject<HTMLDivElement | null>;
  /** Fired on the first measurement and whenever the container crosses `breakpoint`. */
  onBreakpointTransition?: (belowBreakpoint: boolean) => void;
  /**
   * When `true`, an *uncontrolled* panel auto-collapses below the breakpoint
   * and reopens above it (the Sidebar's responsive default). Details passes
   * `false`: it never auto-collapses and honors `defaultCollapsed` everywhere.
   */
  autoCollapse: boolean;
}

interface UseBreakpointCollapseResult {
  /** Most recent measured container width (`0` until first measurement). */
  containerWidth: number;
  /** Current collapsed state (controlled value if provided). */
  collapsed: boolean;
  /** Setter for the collapsed state. */
  setCollapsed: Dispatch<SetStateAction<boolean>>;
  /** True once measured and the container is narrower than `breakpoint`. */
  isBelowBreakpoint: boolean;
}

/**
 * Tracks the container width and fires `onBreakpointTransition` on the first
 * measurement and on every subsequent crossing. Shared by the sidebar and
 * details `useLayoutState` hooks. A crossing never touches a controlled
 * `collapsed`; with `autoCollapse` (the Sidebar) an uncontrolled panel
 * additionally auto-collapses below the breakpoint and reopens above it.
 *
 * @internal
 */
export function useBreakpointCollapse({
  collapsed: collapsedProps,
  breakpoint,
  containerRef,
  onBreakpointTransition,
  autoCollapse,
}: UseBreakpointCollapseArgs): UseBreakpointCollapseResult {
  const [containerWidth, setContainerWidth] = useState(0);

  const [collapsed, setCollapsed] = useControllableState({
    value: collapsedProps.value,
    defaultValue: collapsedProps.defaultValue,
    onChange: collapsedProps.onChange,
  });

  const isControlled = collapsedProps.value !== undefined;
  const emitBreakpointTransition = useCallbackRef(onBreakpointTransition);

  // Tracks which side of the breakpoint the container was on at the previous
  // measurement. `null` until the first measurement so we can distinguish
  // "first ever measurement" from "stayed on the same side".
  const prevBelowBreakpointRef = useRef<boolean | null>(null);

  useResizeObserver({
    ref: containerRef,
    onResize: (size) => {
      if (!size.width) {
        return;
      }

      setContainerWidth(size.width);

      const isBelowBreakpoint = size.width < breakpoint;
      const prevBelowBreakpoint = prevBelowBreakpointRef.current;
      prevBelowBreakpointRef.current = isBelowBreakpoint;

      // Same side as the last measurement: no crossing, nothing to do.
      if (prevBelowBreakpoint === isBelowBreakpoint) {
        return;
      }

      const shouldAutoCollapse = autoCollapse && !isControlled;

      emitBreakpointTransition(isBelowBreakpoint);

      if (prevBelowBreakpoint === null) {
        // First measurement: auto-collapse an uncontrolled Sidebar starting
        // narrow so the nav never covers the content.
        if (shouldAutoCollapse && isBelowBreakpoint && !collapsed) {
          setCollapsed(true);
        }
        return;
      }

      // Only an uncontrolled Sidebar auto-adjusts `collapsed` on crossings;
      // controlled panels and Details are left to the consumer.
      if (shouldAutoCollapse) {
        setCollapsed(isBelowBreakpoint);
      }
    },
  });

  const isBelowBreakpoint = containerWidth !== 0 && containerWidth < breakpoint;

  return { containerWidth, collapsed, setCollapsed, isBelowBreakpoint };
}
